import React, { useEffect, useMemo, useState } from "react";
import EmployeeLayoutV2 from "../../components/EmployeeLayoutV2";
import {
    getBusinessById,
    getEmployeeData,
    getCompletedPayoutsByEmployee,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
    const date = getTimestampDate(value);
    return date ? date.toLocaleString() : "-";
};

export default function EmployeePayoutsV2() {
    const { userData } = useAuth();

    const [business, setBusiness] = useState(null);
    const [employee, setEmployee] = useState(null);
    const [completedPayouts, setCompletedPayouts] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID || !userData?.employeeID) return;

            const [businessData, employeeData, payouts] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeData(userData.businessID, userData.employeeID),
                getCompletedPayoutsByEmployee(userData.businessID, userData.employeeID),
            ]);

            setBusiness(businessData || null);
            setEmployee(employeeData || null);
            setCompletedPayouts(Array.isArray(payouts) ? payouts : []);
        };

        fetchData();
    }, [userData]);

    const sortedPayouts = useMemo(
        () =>
            [...completedPayouts].sort(
                (a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)
            ),
        [completedPayouts]
    );

    const totalPaid = sortedPayouts.reduce((sum, payout) => sum + Number(payout.totalAmount || 0), 0);
    const totalTipsIncluded = sortedPayouts.reduce((sum, payout) => sum + (Array.isArray(payout.tips) ? payout.tips.length : 0), 0);
    const avgPayout = sortedPayouts.length ? totalPaid / sortedPayouts.length : 0;

    return (
        <EmployeeLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .payouts-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .payouts-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --green: #0d7c5f;
                    --amber: #c47a0a;
                    --amber-light: #fef6e6;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    color: var(--text-primary);
                    min-height: 100vh;
                }
                .payouts-v2 .container {
                    width: 100%;
                    max-width: 100%;
                    margin: 0 auto;
                    padding: 32px 0 80px;
                }
                .payouts-v2 .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 26px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .payouts-v2 .title {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }
                .payouts-v2 .subtitle {
                    margin-top: 4px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                .payouts-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .payouts-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .payouts-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .payouts-v2 .stat-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                }
                .payouts-v2 .stat-value {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: -0.8px;
                    font-family: var(--mono);
                    color: var(--green);
                }
                .payouts-v2 .stat-suffix {
                    font-size: 12px;
                    color: var(--text-muted);
                }
                .payouts-v2 .list-shell {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    overflow: hidden;
                }
                .payouts-v2 .list-header {
                    background: var(--surface-raised);
                    border-bottom: 1px solid var(--border);
                    padding: 14px 18px;
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--text-secondary);
                }
                .payouts-v2 .payout-item {
                    padding: 18px;
                    border-bottom: 1px solid var(--border);
                }
                .payouts-v2 .payout-item:last-child { border-bottom: none; }
                .payouts-v2 .top-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .payouts-v2 .amount {
                    font-family: var(--mono);
                    font-size: 24px;
                    font-weight: 700;
                    color: var(--green);
                    letter-spacing: -0.8px;
                }
                .payouts-v2 .emp-name {
                    font-size: 14px;
                    color: var(--text-secondary);
                    margin-top: 4px;
                }
                .payouts-v2 .right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                }
                .payouts-v2 .date {
                    font-size: 12px;
                    color: var(--text-muted);
                }
                .payouts-v2 .status {
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    background: var(--accent-light);
                    color: var(--accent);
                }
                .payouts-v2 .tips-block {
                    margin-top: 8px;
                    background: var(--surface-raised);
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    padding: 10px 12px;
                }
                .payouts-v2 .tips-title {
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--text-secondary);
                    margin-bottom: 8px;
                }
                .payouts-v2 .tip-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 10px;
                    font-size: 12px;
                    padding: 4px 0;
                }
                .payouts-v2 .tip-amount {
                    font-family: var(--mono);
                    color: var(--text-primary);
                }
                .payouts-v2 .tip-date {
                    color: var(--text-muted);
                    text-align: right;
                }
                .payouts-v2 .empty {
                    background: var(--amber-light);
                    border: 1px solid #f0deb3;
                    border-radius: 14px;
                    color: var(--amber);
                    font-weight: 500;
                    font-size: 14px;
                    padding: 16px;
                    text-align: center;
                }
            `}</style>

            <div className="payouts-v2">
                <div className="container">
                    <header className="header">
                        <div>
                            <h1 className="title">My Payouts</h1>
                            <p className="subtitle">
                                {business?.name || userData?.businessID || "Business"} · {employee?.firstName || "Employee"}
                            </p>
                        </div>
                    </header>

                    <div className="stats-grid">
                        <div className="stat-card">
                            <span className="stat-label">Total Paid</span>
                            <span className="stat-value">€{totalPaid.toFixed(2)}</span>
                            <span className="stat-suffix">all payouts</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Payout Count</span>
                            <span className="stat-value">{sortedPayouts.length}</span>
                            <span className="stat-suffix">transactions</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Avg Payout</span>
                            <span className="stat-value">€{avgPayout.toFixed(2)}</span>
                            <span className="stat-suffix">per payout</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-label">Tips Included</span>
                            <span className="stat-value">{totalTipsIncluded}</span>
                            <span className="stat-suffix">across payouts</span>
                        </div>
                    </div>

                    {sortedPayouts.length === 0 ? (
                        <div className="empty">No payouts found.</div>
                    ) : (
                        <div className="list-shell">
                            <div className="list-header">Payout History</div>
                            {sortedPayouts.map((payout) => {
                                const tips = Array.isArray(payout.tips) ? payout.tips : [];
                                return (
                                    <article key={payout.id} className="payout-item">
                                        <div className="top-row">
                                            <div>
                                                <div className="amount">€{Number(payout.totalAmount || 0).toFixed(2)}</div>
                                                <div className="emp-name">{employee?.firstName} {employee?.lastName}</div>
                                            </div>
                                            <div className="right">
                                                <span className="status">{String(payout.status || "pending")}</span>
                                                <span className="date">{formatDateTime(payout.createdAt)}</span>
                                            </div>
                                        </div>

                                        <div className="tips-block">
                                            <p className="tips-title">Included Tips</p>
                                            {tips.length ? (
                                                tips.map((tip) => (
                                                    <div key={tip.id} className="tip-row">
                                                        <span className="tip-amount">€{Number(tip.amount || 0).toFixed(2)}</span>
                                                        <span className="tip-date">{formatDateTime(tip.createdAt)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="tip-row">
                                                    <span className="tip-date">No tip breakdown available</span>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </EmployeeLayoutV2>
    );
}
